import React, { Component } from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import classNames from 'classnames';
import Select from 'react-select';

import BaseModal from '../../components/modals/BaseModal';
import AddQaModal from '../../components/modals/AddQaModal';
import EditQaModal from '../../components/modals/EditQaModal';

import QaCard from '../../components/QaCard';

const customStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    backgroundColor: 'transparent',
    borderColor: '#c3c3c9',
    borderRadius: 2,
    boxShadow: 'none',
    cursor: 'pointer',
    minHeight: 42,

    ':hover': {
      borderColor: '#c3c3c9',
      boxShadow: '0 0 0 2px #e8e8e8',
    },
  }),
  option: (base, { isDisabled, isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isDisabled
      ? null
      : isSelected
      ? '#e8e8e8'
      : isFocused
      ? '#f3f3f5'
      : null,
    color: isDisabled ? '#ccc' : '#4a4a4a',
    cursor: isDisabled ? 'not-allowed' : 'default',
  }),
  menu: (base, { isFocused }) => ({
    ...base,
    borderRadius: 2,
    marginTop: 4,
  }),
};

export const felsen = 'felsen';

export const QA_LIST_QUERY = gql`
  query qas {
    qas {
      id
      questions {
        id
        text
        lang
      }
      slangQuestions {
        id
        text
        lang
      }
      answers {
        id
        text
        lang
      }
      tags {
        id
        name
      }
    }
  }
`;

const QaList = ({
  selectedQaId,
  setActive,
  showAddQaModal,
  showEditQaModal,
}) => (
  <Query
    query={QA_LIST_QUERY}
    fetchPolicy="cache-and-network"
    notifyOnNetworkStatusChange={true}
  >
    {({ loading, error, data, networkStatus }) => {
      if (loading && !data) return null;
      if (error) return null;

      return (
        <div className="qa-list">
          <div className="qa-list-header">
            <div className="qa-list-header-title">Q&A</div>
            <div className="qa-list-header-actions">
              <div className="qa-list-header-action" onClick={showAddQaModal}>
                Lisää QA
              </div>
            </div>
          </div>
          <FilterableQas
            networkStatus={networkStatus}
            qas={data && data.qas}
            showEditQaModal={showEditQaModal}
            loading={loading}
          />
        </div>
      );
    }}
  </Query>
);

const TAGS_QUERY = gql`
  query {
    tags {
      id
      name
    }
  }
`;

class FilterableQas extends Component {
  state = {
    filter: {
      tags: null,
      text: '',
    },
    filteredQas: [],
  };

  handleTextFilterChange = e => {
    e.persist();

    this.setState(state => ({
      filter: {
        ...state.filter,
        text: e.target.value,
      },
    }));
  };

  handleTagFilterChange = tags => {
    this.setState(
      state => ({
        filter: {
          ...state.filter,
          tags,
        },
      }),
      () => {
        this.filterQas();
      }
    );
  };

  filterQas = () => {
    const { qas } = this.props;
    const { filter } = this.state;

    if (!qas) {
      return;
    }

    const filterTags = filter.tags && filter.tags.map(t => t.value);
    const filterText = filter.text;

    let filteredQas = qas;
    // Filter by tags
    if (filter.tags && filter.tags.length > 0) {
      filteredQas = filteredQas.filter(qa => {
        const qaTags = qa.tags.map(t => t.id);

        const hasAllSameTags =
          filterTags.every(filterTag => qaTags.includes(filterTag)) &&
          qaTags.length >= filterTags.length;

        return hasAllSameTags;
      });
    }

    // Filter by text
    filteredQas = filteredQas.filter(qa => {
      if (
        !Array.isArray(qa.questions) ||
        !Array.isArray(qa.slangQuestions) ||
        !Array.isArray(qa.answers)
      ) {
        return false;
      }

      const searchString =
        qa.questions
          .map(q => q.text)
          .join(' ')
          .toLowerCase() +
          qa.slangQuestions
            .map(q => q.text)
            .join(' ')
            .toLowerCase() +
          qa.answers
            .map(q => q.text)
            .join(' ')
            .toLowerCase() || '';

      return searchString.includes(filterText.toLowerCase());
    });

    this.setState({
      filteredQas,
    });
  };

  componentDidMount() {
    this.filterQas();
  }

  componentDidUpdate(prevProps, prevState) {
    const { filter } = this.state;
    const { filter: prevFilter } = prevState;

    const { loading } = this.props;
    const { loading: prevLoading } = prevProps;

    if (filter.text !== prevFilter.text) {
      this.filterQas();
    }

    if (prevLoading && !loading) {
      this.filterQas();
    }
  }

  render() {
    const { showEditQaModal } = this.props;
    const { filteredQas, filter } = this.state;

    return (
      <div className="qa-list-items">
        <div className="qa-list-items-filter">
          <input
            className="qa-list-items-filter-text"
            type="text"
            id="filter"
            value={filter.text}
            onChange={this.handleTextFilterChange}
          />
          <Query query={TAGS_QUERY}>
            {({ loading, error, data }) => {
              if (error) return `Error! ${error.message}`;

              return (
                <Select
                  id="tags"
                  className="qa-list-items-filter-tags"
                  placeholder="Suodata tageilla"
                  styles={customStyles}
                  isDisabled={loading}
                  isLoading={loading}
                  options={
                    data.tags
                      ? data.tags.map(tag => ({
                          value: tag.id,
                          label: tag.name,
                        }))
                      : []
                  }
                  isMulti
                  onChange={this.handleTagFilterChange}
                  value={filter.tags}
                />
              );
            }}
          </Query>
        </div>
        <div className="qa-list-items-grid">
          {filteredQas &&
            filteredQas.map((qa, i) => {
              return (
                <QaCard
                  key={`${qa.id}-${i}`}
                  {...qa}
                  onClick={showEditQaModal(qa.id)}
                />
              );
            })}
        </div>
      </div>
    );
  }
}

const QA_QUERY = gql`
  query Qa($id: Int!) {
    qa(id: $id) {
      id
      questions {
        id
        text
        lang
      }
      slangQuestions {
        id
        text
        lang
      }
      answers {
        id
        text
        lang
      }
    }
  }
`;

const Qa = ({ id }) => {
  if (!id) {
    return <div className="qa-info" />;
  }

  return (
    <Query query={QA_QUERY} fetchPolicy="cache-first" variables={{ id }}>
      {({ loading, error, data }) => {
        if (loading) return null;
        if (error) return null;

        return <div className="qa-info">{data.qa.id}</div>;
      }}
    </Query>
  );
};

class QaRoute extends Component {
  state = {
    modals: {
      addQa: false,
      editQa: false,
    },
  };

  showAddQaModal = e => {
    e.preventDefault();

    this.setState(state => ({
      modals: {
        ...state.modals,
        addQa: true,
      },
    }));
  };

  hideAddQaModal = () => {
    this.setState(state => ({
      modals: {
        ...state.modals,
        addQa: false,
      },
    }));
  };

  showEditQaModal = (id, callback) => e => {
    e.preventDefault();

    this.setState(state => ({
      modals: {
        ...state.modals,
        editQa: id,
      },
    }));
  };

  hideEditQaModal = () => {
    this.setState(state => ({
      modals: {
        ...state.modals,
        editQa: false,
      },
    }));
  };

  render() {
    const { modals } = this.state;

    return (
      <div className="qa">
        <QaList
          showAddQaModal={this.showAddQaModal}
          showEditQaModal={this.showEditQaModal}
        />
        {modals.addQa && (
          <BaseModal maxWidth={576} minWidth={340}>
            <AddQaModal closeModal={this.hideAddQaModal} />
          </BaseModal>
        )}
        {modals.editQa && (
          <BaseModal maxWidth={576} minWidth={340}>
            <EditQaModal id={modals.editQa} closeModal={this.hideEditQaModal} />
          </BaseModal>
        )}
      </div>
    );
  }
}

export default QaRoute;
